import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import TimelineProvider from '@src/new-components/Timeline/TimelineProvider';
import TimelineGrid from '@src/new-components/Timeline/TimelineGrid';
import TimelineToolbar from '@src/new-components/Timeline/TimelineToolbar';
import TimelineRuler from '@src/new-components/Timeline/TimelineRuler';
import PlayheadOverlay from '@src/new-components/Timeline/PlayheadOverlay';
import PlayheadLine from '@src/new-components/Timeline/PlayheadLine';
import ProgressSlider from '@src/new-components/Timeline/ProgressSlider';
import TrackGutter from '@src/new-components/Timeline/TrackGutter';
import useTimeline from '@src/hooks/useTimeline';
import useTimelineSeek from '@src/hooks/useTimelineSeek';
import { getSourceUrl } from '@src/services/project';
import { readVideoMeta } from '@src/services/videoMetaStore';
import {
  DEFAULT_ZOOM,
  MEDIA_TRACK_HEIGHT,
  TIMELINE_LANE,
  VOLUME_TRACK_BY_LANE,
} from '@src/constants/timeline';
import {
  clamp,
  computeEdgeScrollVelocity,
  getBlocksMaxEndSec,
  isSeekSurface,
} from '@src/services/timeline';
import MediaTrack from './MediaTrack';
import VoiceoverTrack from './VoiceoverTrack';
import MusicTrack from './MusicTrack';
import useTrackVolume from './useTrackVolume';
import {
  StyledBody,
  StyledGutterColumn,
  StyledGutterSpacer,
  StyledRulerLine,
  StyledScrollColumn,
  StyledTimeline,
} from './index.style';

const DEFAULT_DURATION = 30;

// Tracks whose gutter opens a volume popover (mute + level). Media and voice
// affect the editor preview; music stores its level for when BGM preview lands.
const VOLUME_POPOVER_TRACKS = new Set(Object.keys(VOLUME_TRACK_BY_LANE));

const TimelineInner = ({
  durationSec,
  onHeightChange,
  currentSec = 0,
  isPlaying = false,
  getCurrentSec,
  onPlayPause,
  onSeek,
  isAudioChanging = false,
  hasProjectAudio = false,
  createAudioTooltip,
  onCreateAudio,
  isCreateAudioDisabled = false,
  isCreateAudioHidden = false,
  isConverting = false,
  synthesisProgress = 0,
  onAddBlock,
  onInsertBlock,
  isAddBlockDisabled = false,
  blackWordsList = [],
  isExtracting = false,
  revealBlockId = null,
  onSelectBlock,
  isLocked = false,
}) => {
  const { t } = useTranslation();
  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    setZoomLevel,
    fit,
    viewportWidth,
    pxToSec,
    secToPx,
    viewportRef,
    scrollViewportBy,
  } = useTimeline();
  const [collapsed, setCollapsed] = useState(false);
  const { isMuted, getVolume, setVolume, toggleMute, commitVolume } =
    useTrackVolume();

  // Drag / click to seek. Owned here (not in the overlay) so BOTH the line —
  // which lives inside the scrolling content — and the overlay time chip share
  // the same live scrub position. The pointer is confined to the viewport and
  // auto-scrolls at the edges to reveal content beyond the window.
  const toSec = useCallback(
    (clientX) => {
      const el = viewportRef.current;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const clampedX = clamp(clientX, rect.left, rect.right);
      const contentX = clampedX - rect.left + el.scrollLeft;
      return clamp(pxToSec(contentX), 0, durationSec);
    },
    [pxToSec, durationSec, viewportRef],
  );

  const autoScroll = useMemo(
    () => ({
      getVelocity: (clientX) => {
        const el = viewportRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        return computeEdgeScrollVelocity(clientX, rect.left, rect.width);
      },
      scrollBy: (deltaPx) => scrollViewportBy(deltaPx),
    }),
    [viewportRef, scrollViewportBy],
  );

  const { onPointerDown, scrubSec, scrubTo, endScrub } = useTimelineSeek({
    onSeek,
    toSec,
    autoScroll,
  });

  // The WHOLE scrolling surface seeks — every lane's empty space and the gaps
  // between them, not just the ruler and the media track. One handler on the
  // shared column (rather than one per lane) means a lane added later is
  // seekable for free, and it catches the space no lane covers. Elements that
  // own their own gesture (clips, the append buttons) are skipped, so pressing
  // one still only selects/drags it — see isSeekSurface.
  const handleSurfacePointerDown = useCallback(
    (event) => {
      // React events travel the React tree, not the DOM one: a lane's portalled
      // children — the BGM picker dialog, a clip's right-click menu — render into
      // document.body yet their presses still bubble to this handler. They aren't
      // on the timeline surface at all, so only accept a real DOM descendant of
      // the column. Otherwise pressing a dialog button scrubs the playhead, and
      // the re-render that follows swallows the click that should have followed.
      if (!event.currentTarget.contains(event.target)) return;
      if (!isSeekSurface(event.target)) return;
      onPointerDown(event);
    },
    [onPointerDown],
  );

  const isScrubbing = scrubSec != null;
  const playheadSec = isScrubbing ? scrubSec : currentSec;
  const playheadPlaying = isPlaying && !isScrubbing;

  // One source of truth for the tracks — the gutter cells and the lanes are
  // both generated from this, so they always stay aligned 1-1. Adding Media /
  // Music later is a single entry each (Lane = the track's lane component).
  //
  // The labels have their own `timelineTrack*` keys rather than reusing the
  // general `media` / `voice` / `backgroundMusic` ones: those are shared with
  // the TTS screens (and reached through dynamic `t(variable)` calls), so
  // wording them for this gutter — short, and "Video" for the media track —
  // would change text elsewhere.
  const tracks = useMemo(
    () => [
      {
        key: TIMELINE_LANE.MEDIA,
        icon: <PermMediaRoundedIcon />,
        label: t('timelineTrackVideo'),
        Lane: MediaTrack,
        // Taller than the other tracks — frames + a waveform band under them.
        height: MEDIA_TRACK_HEIGHT,
      },
      {
        key: TIMELINE_LANE.VOICE,
        icon: <GraphicEqRoundedIcon />,
        label: t('timelineTrackVoice'),
        Lane: VoiceoverTrack,
      },
      {
        key: TIMELINE_LANE.MUSIC,
        icon: <MusicNoteRoundedIcon />,
        label: t('timelineTrackBgm'),
        Lane: MusicTrack,
      },
    ],
    [t],
  );

  // Per-lane wiring, kept out of the memo so its handlers don't invalidate it.
  // `isLocked` freezes every clip edit while a download/export is in flight —
  // transport (play, seek, zoom) and selecting a clip stay live, since neither
  // changes what the export is building from.
  const lanePropsByKey = {
    [TIMELINE_LANE.VOICE]: {
      onAddBlock,
      onInsertBlock,
      isAddBlockDisabled: isAddBlockDisabled || isLocked || isPlaying,
      blackWordsList,
      isExtracting,
      revealBlockId,
      onSelectBlock,
      isLocked,
      // Trimming a clip walks the playhead along the edge being dragged, so the
      // user hears/sees where the new in- or out-point lands while placing it.
      onScrubTo: scrubTo,
      onScrubEnd: endScrub,
      // Playing freezes the track the same way the export lock does — no
      // select, move, trim, menu or append, and the cursor says so. The track
      // must not change under the audio being heard, and focus belongs to the
      // playhead for the duration.
      isPlaying,
    },
    // The BGM track is locked by the same export lock and, additionally, while
    // the transcript is still being extracted: the project has no content to
    // score yet (design keeps the add button dimmed).
    [TIMELINE_LANE.MUSIC]: {
      isLocked: isLocked || isExtracting,
      isPlaying,
    },
  };

  // Report the panel's real height so the editor can reserve exactly that much
  // — collapsed = toolbar only, expanded = toolbar + tracks. Re-measures on
  // `collapsed` toggle (not just via ResizeObserver) so the reserve never lags.
  //
  // MUST be a layout effect. The editor's height is `100vh - reserve`, so on
  // expand the dock grows in the same commit while the reserve still holds the
  // collapsed height: editor + dock then overrun the viewport. Measuring in a
  // passive effect lets the browser paint that overrun before the reserve
  // catches up — one frame of the dock sitting too low, then snapping up. A
  // layout effect measures and reports before the paint, so the expand lands in
  // a single frame. (Collapse hid it: there the intermediate frame is just an
  // empty strip that fills in, nothing moves.)
  const rootRef = useRef(null);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || !onHeightChange || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => onHeightChange(el.offsetHeight));
    observer.observe(el);
    onHeightChange(el.offsetHeight);
    return () => observer.disconnect();
  }, [onHeightChange, collapsed]);

  // Keep the whole duration fitted to the viewport until the user zooms
  // manually — covers the async settle of viewport width and video duration.
  const userZoomedRef = useRef(false);
  useEffect(() => {
    if (!userZoomedRef.current && viewportWidth > 0) fit();
  }, [viewportWidth, durationSec, fit]);

  // Follow the playhead while playing: page the viewport forward when it nears
  // the right edge. If the user scrolls by hand, stop following (a scrollLeft we
  // didn't set = the user) so we don't yank it back = jitter; resume when the
  // playhead is back in view. State lives in refs because `secToPx` changes
  // identity on every scroll (re-running the loop effect), which must NOT reset a
  // user-scroll suspend — only a fresh play resets it.
  const followingRef = useRef(true);
  const lastScrollLeftRef = useRef(null);
  useEffect(() => {
    if (!playheadPlaying) return;
    followingRef.current = true;
    lastScrollLeftRef.current = null;
  }, [playheadPlaying]);

  useEffect(() => {
    if (!playheadPlaying || !getCurrentSec) return undefined;
    let rafId = 0;
    const MARGIN_PX = 48;
    const tick = () => {
      const el = viewportRef.current;
      if (el) {
        const left = el.scrollLeft;
        if (
          followingRef.current &&
          lastScrollLeftRef.current !== null &&
          left !== lastScrollLeftRef.current
        ) {
          followingRef.current = false; // scrolled by the user, not our paging
        }
        const playheadX = secToPx(getCurrentSec());
        const right = left + viewportWidth;
        if (followingRef.current) {
          if (playheadX < left || playheadX > right - MARGIN_PX) {
            // Land the playhead near the left edge for a fresh screen of lead.
            scrollViewportBy(playheadX - left - MARGIN_PX);
          }
          lastScrollLeftRef.current = el.scrollLeft;
        } else if (
          playheadX >= left + MARGIN_PX &&
          playheadX <= right - MARGIN_PX
        ) {
          followingRef.current = true;
          lastScrollLeftRef.current = left;
        }
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [
    playheadPlaying,
    getCurrentSec,
    secToPx,
    viewportWidth,
    viewportRef,
    scrollViewportBy,
  ]);

  const handleZoomIn = () => {
    userZoomedRef.current = true;
    zoomIn();
  };
  const handleZoomOut = () => {
    userZoomedRef.current = true;
    zoomOut();
  };
  const handleZoomChange = (value) => {
    userZoomedRef.current = true;
    setZoomLevel(value);
  };
  const handleFit = () => {
    userZoomedRef.current = false;
    fit();
  };

  return (
    <StyledTimeline ref={rootRef}>
      {/* Collapsed hides the ruler, the tracks AND the playhead — i.e. every
          way to see or set the position. This strip (the audio player's
          slider) stands in for them until the panel is opened again. */}
      {collapsed && (
        <ProgressSlider
          sec={playheadSec}
          playing={playheadPlaying}
          getSec={getCurrentSec}
          durationSec={durationSec}
          onSeek={onSeek}
          disabled={isAudioChanging}
        />
      )}
      <TimelineToolbar
        collapsed={collapsed}
        hideTopBorder={collapsed}
        onToggleCollapse={() => setCollapsed((prev) => !prev)}
        onCreateAudio={onCreateAudio}
        createAudioLabel={t(hasProjectAudio ? 'reSynthesize' : 'generateAudio')}
        createAudioTooltip={createAudioTooltip}
        convertingLabel={t('creatingAudioProgress', {
          progress: synthesisProgress,
        })}
        isCreateAudioDisabled={isCreateAudioDisabled}
        isCreateAudioHidden={isCreateAudioHidden}
        isConverting={isConverting}
        isPlaying={isPlaying}
        onPlayPause={onPlayPause}
        isPlayDisabled={isAudioChanging}
        currentSec={currentSec}
        getSec={getCurrentSec}
        durationSec={durationSec}
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomChange={handleZoomChange}
        onFit={handleFit}
      />
      {!collapsed && (
        <StyledBody>
          <StyledRulerLine position="top" />
          <StyledRulerLine position="bottom" />
          <TimelineGrid
            renderGutter={
              <StyledGutterColumn>
                <StyledGutterSpacer />
                {tracks.map(({ key, icon, label, height }) => {
                  const hasVolume = VOLUME_POPOVER_TRACKS.has(key);
                  return (
                    <TrackGutter
                      key={key}
                      icon={icon}
                      label={label}
                      height={height}
                      muted={isMuted(key)}
                      onToggleMute={() => toggleMute(key)}
                      volume={hasVolume ? getVolume(key) : undefined}
                      onVolumeChange={
                        hasVolume ? (value) => setVolume(key, value) : undefined
                      }
                      onVolumeCommit={hasVolume ? commitVolume : undefined}
                      volumeDisabled={isLocked}
                    />
                  );
                })}
              </StyledGutterColumn>
            }
          >
            <StyledScrollColumn onPointerDown={handleSurfacePointerDown}>
              <TimelineRuler />
              {tracks.map(({ key, Lane }) => (
                <Lane key={key} {...(lanePropsByKey[key] || {})} />
              ))}
              <PlayheadLine
                sec={playheadSec}
                playing={playheadPlaying}
                getSec={getCurrentSec}
              />
            </StyledScrollColumn>
          </TimelineGrid>
          <PlayheadOverlay
            sec={playheadSec}
            playing={playheadPlaying}
            getSec={getCurrentSec}
            onSeekPointerDown={onPointerDown}
          />
        </StyledBody>
      )}
    </StyledTimeline>
  );
};

/**
 * DubbingTimeline (L3 orchestrator). Timeline duration spans at least the
 * video, extended to the last voice block so no clip is clipped off.
 *
 * Transport (play/pause, current time) and create-audio come from the Content
 * container, which owns the single audio engine + all synthesis validation —
 * re-instantiating them here would double-mount the audio element. This
 * component only threads them into the (presentational) toolbar.
 */
const DubbingTimeline = ({
  durationSec,
  onHeightChange,
  currentSec,
  isPlaying,
  getCurrentSec,
  onPlayPause,
  onSeek,
  isAudioChanging,
  hasProjectAudio,
  createAudioTooltip,
  onCreateAudio,
  isCreateAudioDisabled,
  isCreateAudioHidden,
  isConverting,
  synthesisProgress,
  onAddBlock,
  onInsertBlock,
  isAddBlockDisabled,
  blackWordsList,
  isExtracting,
  revealBlockId,
  onSelectBlock,
  isLocked,
}) => {
  const { blocks, blockIds } = useSelector((state) => state.project.multiBlock);
  const musicClips = useSelector((state) => state.project.musicClips);
  const source = useSelector((state) => state.project.source);
  // The video's own length, from an earlier visit. `durationSec` comes from the
  // player, which only reports it once the element has loaded metadata — so on a
  // reload the panel would otherwise lay itself out against DEFAULT_DURATION and
  // snap once the video answered, jumping every tick and clip on the way. Read
  // synchronously (localStorage, not the frame cache) so the FIRST render has it.
  const cachedDurationSec =
    readVideoMeta(getSourceUrl(source))?.durationSec ?? 0;
  const blocksEndSec = useMemo(
    () => getBlocksMaxEndSec(blocks, blockIds),
    [blocks, blockIds],
  );
  // The timeline spans the longest track so no clip (voice or music) is cut off
  // and Fit can shrink to show them all.
  const musicEndSec = useMemo(
    () => musicClips.reduce((max, clip) => Math.max(max, clip.endSec), 0),
    [musicClips],
  );
  const resolved =
    Math.max(durationSec || 0, cachedDurationSec, blocksEndSec, musicEndSec) ||
    DEFAULT_DURATION;

  return (
    <TimelineProvider durationSec={resolved} initialZoom={DEFAULT_ZOOM}>
      <TimelineInner
        durationSec={resolved}
        onHeightChange={onHeightChange}
        currentSec={currentSec}
        isPlaying={isPlaying}
        getCurrentSec={getCurrentSec}
        onPlayPause={onPlayPause}
        onSeek={onSeek}
        isAudioChanging={isAudioChanging}
        hasProjectAudio={hasProjectAudio}
        createAudioTooltip={createAudioTooltip}
        onCreateAudio={onCreateAudio}
        isCreateAudioDisabled={isCreateAudioDisabled}
        isCreateAudioHidden={isCreateAudioHidden}
        isConverting={isConverting}
        synthesisProgress={synthesisProgress}
        onAddBlock={onAddBlock}
        onInsertBlock={onInsertBlock}
        isAddBlockDisabled={isAddBlockDisabled}
        blackWordsList={blackWordsList}
        isExtracting={isExtracting}
        revealBlockId={revealBlockId}
        onSelectBlock={onSelectBlock}
        isLocked={isLocked}
      />
    </TimelineProvider>
  );
};

export default DubbingTimeline;
